import { ExstTranslatorHelper } from './exst-translator-helper';
import type { FilterChildResponse } from '../../../models/cose-api/response/filter-response.model';
import chunks from './chunks-generator';
import translatedArrayGenerator from './translated-array-generator';

export interface ResponsePartial {
    [key: string]: ResponsePartial | ResponsePartial[] | number | string | boolean;
}

export class ExstResponseTranslator {
  static translated = {};
  static haveToTranslate = [];
  static translateInterval;
  static translateAwaitTime = 10000;
  static chunkSize = 80;
  static checkTime = 10;
  static translationsAndpoint: string;
  static pageUrl: string;
  static language: string;
  static defaultLanguage: string;
  static exceptFromFromTranslateFacetsId = [];
  private static requestPeriodTime = 100;

  static setConfig(config: Record<string, any>) {  //eslint-disable-line
      Object.assign(ExstResponseTranslator, config);
  }

  static translateIntervalInit() {
      if(!ExstResponseTranslator.translateInterval) {
        ExstResponseTranslator.translateInterval = setInterval(() => { //request animation frame, albo setInterval
            const missingTranslates = ExstResponseTranslator.haveToTranslate.filter((toTranslateElement) => {
                let bool = true;
                Object.keys(ExstResponseTranslator.translated).forEach((encodedTranslate) => {
                    const decodedTranslate = ExstTranslatorHelper.decodeTextFromResponse(encodedTranslate);
                    if (decodedTranslate === toTranslateElement) {
                      bool = false;
                    }
                });
                return bool;
            });
            const localToTranslate = [...missingTranslates];
            ExstResponseTranslator.haveToTranslate = [];
            if (localToTranslate.length > 0) {
              [...chunks(localToTranslate, ExstResponseTranslator.chunkSize)]
                .forEach(partial => {
                    ExstResponseTranslator.getTranslations(partial)
                      .then(data => {
                          for (const [key, value] of Object.entries(data.translatedText)) {
                            translatedArrayGenerator.next([key, value]);
                          }
                      });
                });
            }
        }, ExstResponseTranslator.requestPeriodTime);
      }
  }

  static getTranslations(partial) {
      return fetch(ExstResponseTranslator.translationsAndpoint, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          'translationDirection': ExstResponseTranslator.language,
          'pageUrl': ExstResponseTranslator.pageUrl,
          'source': partial.map(line => ExstTranslatorHelper.encodeTextToSend(line))
        })
      })
        .then(response => response.json());
  }

  static restartTranslateInterval() {
      if(ExstResponseTranslator.translateInterval) {
        clearInterval(ExstResponseTranslator.translateInterval);
        ExstResponseTranslator.translateInterval = undefined;
      }
      ExstResponseTranslator.translateIntervalInit();
  }

  static async translate(input: string){
      ExstResponseTranslator.haveToTranslate.push(input);
      const translatedValue = await ExstResponseTranslator.getPossibleTranslatedValue(ExstTranslatorHelper.encodeTextToSend(input));
      return ExstTranslatorHelper.decodeTextFromResponse(translatedValue);
  }

  static async getPossibleTranslatedValue(input: string) {
      let interval;
      const waitForTranslateReturn = new Promise((resolve)=>{
          interval = setInterval(()=>{ //albo requestAnimationFrame
              if(ExstResponseTranslator.translated[input]){
                clearInterval(interval);
                resolve(ExstResponseTranslator.translated[input]);
                interval = null;
              }
          }, ExstResponseTranslator.checkTime);
      });
      const translatedOrNot = await Promise.race([
        new Promise(r => setTimeout(()=>{
            if(interval) {
              clearInterval(interval);
            }
            return r(input);
        },ExstResponseTranslator.translateAwaitTime)),
        waitForTranslateReturn
      ]);
      return translatedOrNot as string;
  };

  static async translatedDocsBuilder(response: any) { //eslint-disable-line
      return await Promise.allSettled(response.documents.map(async d => {
          let newDocument = {
            ...d,
            title: await ExstResponseTranslator.translate(d.title),
          };
          if(d?.description) {
            newDocument = {
              ...newDocument,
              description: await ExstResponseTranslator.translate(newDocument.description)
            };
          }
          if(d?.publisherDescription) {
            newDocument = {
              ...newDocument,
              publisherDescription: await ExstResponseTranslator.translate(newDocument.publisherDescription)
            };
          }
          if(d?.label && d?.label?.name) {
            newDocument = {
              ...newDocument,
              label: {
                name: await ExstResponseTranslator.translate(d?.label?.name),
              }
            };
          }
          if(d?.category && typeof d.category === 'string') {
            newDocument = {
              ...newDocument,
              category: await ExstResponseTranslator.translate(newDocument.category)
            };
          }
          if(d?.categoryList) {
            newDocument = {
              ...newDocument,
              categoryList: []
            };
            d.categoryList.forEach(async (c) => {
                newDocument.categoryList.push({name:await ExstResponseTranslator.translate(c.name)});
            });
          }
          if(d?.subcategoryList) {
            newDocument = {
              ...newDocument,
              subcategoryList: []
            };
            d.subcategoryList.forEach(async (c) => {
                newDocument.subcategoryList.push({name:await ExstResponseTranslator.translate(c.name)});
            });
          }
          return newDocument;
      }));
  }

  static async translatedFacetsBuilder(response: any) { //eslint-disable-line
      async function recurseFiltersTranslateBuilder(filters: FilterChildResponse[]) {
          return (await Promise.allSettled(filters.map(async filter => {
              let children = [];
              if(filter.children.length > 0) {
                children = await recurseFiltersTranslateBuilder(filter.children);
              }
              const newFilter = {
                ...filter,
                displayName: await ExstResponseTranslator.translate(filter['displayName'] as string),
                children
              };

              return newFilter;
          }))).filter(p => p.status == 'fulfilled').map(p=>(p as any).value);  //eslint-disable-line
      }

      return await Promise.allSettled(response.facetsOptions.facets.map(async facet => {
          let childFilters = facet.children;
          if(!ExstResponseTranslator.exceptFromFromTranslateFacetsId.includes(facet.id)) {
            childFilters = (await recurseFiltersTranslateBuilder(facet.children));
          }

          const newFacet = {
            ...facet,
            displayName: await ExstResponseTranslator.translate(facet.displayName),
            children: childFilters
          };
          return newFacet;
      }));
  }
}
